import { Signal, computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { StandardApiResponse, StandardPagedData } from '../interfaces/standard-api-response.interface';
import { ApiClientService } from '../services/api-client.service';
import { API_BASE_URL } from '../tokens/api-base-url.token';
import { extractPagedItems } from './api-response.utils';

export interface DropdownOption {
  id: string;
  name: string;
}

export interface DropdownOptionsConfig<TRecord, TOut = TRecord> {
  /**
   * Either a fixed path, or a reactive accessor returning the path to use.
   * The accessor is re-evaluated whenever any signal it reads changes; returning
   * `undefined` pauses loading (used for fields that depend on another field's
   * selection, e.g. townships-by-district, until that selection is made).
   */
  path: string | (() => string | undefined);
  mapItem?: (record: TRecord) => TOut;
  params?: () => Record<string, string | number | boolean> | undefined;
  pageSize?: number;
  paramStyle?: 'header' | 'query';
  /**
   * 'service' (default) resolves against API_BASE_URL (e.g. `.../deeds/`).
   * 'gateway' strips that trailing service segment to hit endpoints mounted
   * directly on the gateway root instead (e.g. `/api/v2/districts`, which is
   * NOT under `/deeds`).
   */
  base?: 'service' | 'gateway';
}

export function loadDropdownOptions<TRecord, TOut = TRecord>(
  config: DropdownOptionsConfig<TRecord, TOut>,
): { options: Signal<TOut[]>; loading: Signal<boolean> } {
  const api = inject(ApiClientService);
  const baseUrl = inject(API_BASE_URL);
  const normalizedServiceBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const gatewayBase = normalizedServiceBase.replace(/\/[^/]+$/, '');
  const normalizedBase = config.base === 'gateway' ? gatewayBase : normalizedServiceBase;
  const pageSize = config.pageSize ?? 10;
  const paramStyle = config.paramStyle ?? 'header';

  const buildUrl = (path: string): string => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  const resource = rxResource({
    params: () => {
      const path = typeof config.path === 'function' ? config.path() : config.path;
      if (!path) {
        return undefined;
      }
      const rest = (config.params ?? (() => ({})))();
      if (rest === undefined) {
        return undefined;
      }
      return { ...rest, __path: path };
    },
    stream: ({ params }) => {
      if (params === undefined) {
        return of(null);
      }
      const { __path, ...rest } = params;
      const requestParams = { pageNumber: 1, pageSize, ...rest };
      return api.get<StandardApiResponse<StandardPagedData<TRecord>>>(
        buildUrl(__path as string),
        paramStyle === 'query' ? { params: requestParams } : { headers: requestParams },
      );
    },
  });

  const options = computed(() => {
    const response = resource.value();
    if (!response) {
      return [];
    }
    const items = extractPagedItems<TRecord>(response);
    return config.mapItem ? items.map(config.mapItem) : (items as unknown as TOut[]);
  });

  return { options, loading: resource.isLoading };
}
